'use client';

/**
 * Shared chrome for the full-height blocks ({@code HeroFullHeightBanner},
 * {@code FullHeightStats}, {@code FullHeightDiscovery},
 * {@code FullHeightPodcast}): a section that fills the visible viewport
 * under the site header, stacks its slides in one grid cell, and — with two
 * or more slides — pins prev/next arrows and dots to the bottom centre.
 *
 * <p>Each block owns what a slide LOOKS like ({@code renderSlide}) and which
 * slides exist (content + campaign filtering); this owns everything that
 * makes it a carousel: wrap-around arrows, dots, touch swipe,
 * Left/Right/Home/End keys, optional autoplay (paused on hover, focus,
 * hidden tab and reduced motion), inactive slides {@code inert} +
 * {@code aria-hidden}, and the polite "Slide N of M" live region.
 *
 * <p>"Full height" is the viewport MINUS the site chrome above the block,
 * measured the same way {@code ShortsFeed} does: the shell publishes no
 * general header-height variable, and a flat 100dvh under a header would
 * push the content's vertical centre below the fold line.
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type TouchEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { BlockRoot } from './BlockRoot';
import { HeroEmptyFallback } from './HeroEmptyFallback';

export interface FullHeightSliderSettings {
  /** Honoured only with 2+ slides AND no reduced-motion. */
  autoplay?: boolean;
  /** Time per slide in ms. Min 2000 enforced at render. */
  autoplayDelay?: number;
  /** `slide` = horizontal translate; `fade` = opacity crossfade. */
  transition?: 'slide' | 'fade';
  /** Default true. Ignored with fewer than 2 slides. */
  showArrows?: boolean;
  /** Default true. Ignored with fewer than 2 slides. */
  showDots?: boolean;
}

export interface FullHeightSlideContext {
  active: boolean;
  /** Arrows and/or dots are pinned under the content — pass to
   *  {@link FullHeightFrame} so it reserves room for them. */
  hasControls: boolean;
}

export interface FullHeightSliderProps<T> extends FullHeightSliderSettings {
  /** Already filtered to the slides that should render. */
  slides: T[];
  renderSlide: (slide: T, index: number, ctx: FullHeightSlideContext) => ReactNode;
  /** Painted behind every slide and NOT moved by the slide track — a glow
   *  or a shared background. */
  backdrop?: ReactNode;
  /** Section background (under the backdrop and the slides). */
  background?: string;
}

const SWIPE_THRESHOLD = 50;
// Below this horizontal motion the gesture is a vertical scroll — leave it
// to the browser.
const HORIZONTAL_LOCK_RATIO = 1.2;

// Viewport units, hover/focus states and the breakpoint can't live in an
// inline style object, so they go in a scoped stylesheet (same convention
// as proof/Testimonials). The vh line is the fallback for engines without
// dvh, not a duplicate. `data-controls` frames reserve room under the
// content for the pinned arrows + dots (65px tall) and stay symmetric so
// the content keeps its vertical centre.
const FULL_HEIGHT_SLIDER_CSS = `
.fh-slider-frame {
  min-height: calc(100vh - var(--fh-slider-offset, 0px));
  min-height: calc(100dvh - var(--fh-slider-offset, 0px));
  padding-block: 40px;
  padding-inline: max(16px, 11%);
}
.fh-slider-frame[data-controls] { padding-block: 80px; }
.fh-slider-arrow > span { background-color: rgba(255, 255, 255, 0.18); transition: background-color 150ms ease; }
.fh-slider-arrow:hover > span { background-color: rgba(255, 255, 255, 0.3); }
.fh-slider-dot > span { background-color: rgba(255, 255, 255, 0.2); transition: width 220ms ease, background-color 150ms ease; }
.fh-slider-dot:hover > span { background-color: rgba(255, 255, 255, 0.4); }
.fh-slider-arrow:focus-visible,
.fh-slider-dot:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 1px;
  border-radius: 999px;
}
@media (max-width: 767px) {
  .fh-slider-frame { padding-inline: 16px; }
}
`;

const controlReset: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: '#ffffff',
  cursor: 'pointer',
};

export function FullHeightSlider<T>({
  slides,
  renderSlide,
  backdrop,
  background = '#10131a',
  autoplay = false,
  autoplayDelay = 6000,
  transition = 'slide',
  showArrows = true,
  showDots = true,
}: FullHeightSliderProps<T>) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const viewportRef = useRef<HTMLDivElement>(null);

  const count = slides.length;
  const isCarousel = count > 1;
  const hasControls = isCarousel && (showArrows || showDots);

  const [rawIndex, setIndex] = useState(0);
  // The slide list can shrink under us (an expiry, an admin edit).
  const index = count > 0 ? Math.min(rawIndex, count - 1) : 0;
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageHidden, setPageHidden] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    const sync = () => setPageHidden(document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  // Re-armed on every index change so the timer never drifts.
  useEffect(() => {
    if (!isCarousel || !autoplay || reducedMotion || paused || pageHidden) return;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % count), Math.max(2000, autoplayDelay));
    return () => clearTimeout(timer);
  }, [index, isCarousel, autoplay, reducedMotion, paused, pageHidden, autoplayDelay, count]);

  // ── Fit the viewport MINUS the chrome above us ────────────────────────────
  // Measured against the document (not the viewport) so scroll position
  // doesn't move it. Written straight to a CSS variable the slide frames
  // inherit — no React state, so a resize never re-renders the block.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // A slider deep in a long page isn't "under the header" — past half a
      // viewport, treat it as a plain full-height section.
      const offset = top > 0 && top < window.innerHeight * 0.5 ? Math.round(top) : 0;
      el.style.setProperty('--fh-slider-offset', `${offset}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    // Chrome above us can change height after mount (a header that
    // hydrates, a font swap, a collapsing top bar).
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [count]);

  const wrap = (n: number) => ((n % count) + count) % count;
  const go = (target: number) => {
    if (isCarousel) setIndex(wrap(target));
  };
  // Stepped from the latest index, not this render's — two quick presses
  // before a re-render must move two slides, not one.
  const step = (delta: number) => {
    if (isCarousel) setIndex((i) => wrap(Math.min(i, count - 1) + delta));
  };
  const prev = () => step(-1);
  const next = () => step(1);

  // Decide on touchend; never preventDefault on touchmove (that would block
  // vertical page scrolling).
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (isCarousel && touch) touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const touch = e.changedTouches[0];
    if (!isCarousel || !start || !touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dy) * HORIZONTAL_LOCK_RATIO > Math.abs(dx) || Math.abs(dx) < SWIPE_THRESHOLD) {
      return;
    }
    if (dx < 0) next();
    else prev();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isCarousel) return;
    const moves: Record<string, () => void> = {
      ArrowLeft: prev,
      ArrowRight: next,
      Home: () => go(0),
      End: () => go(count - 1),
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    move();
  };

  if (count === 0) {
    return <HeroEmptyFallback style={{ width: '100%', height: 'min(100dvh, 900px)' }} />;
  }

  const fade = transition === 'fade' || reducedMotion;

  return (
    <BlockRoot
      as="section"
      role={isCarousel ? 'region' : undefined}
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={isCarousel ? t('hero.carousel.featuredBanner') : undefined}
      tabIndex={isCarousel ? -1 : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        outline: 'none',
        background,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: FULL_HEIGHT_SLIDER_CSS }} />

      {backdrop ? (
        <div
          aria-hidden
          style={{ position: 'absolute', inset: 0, zIndex: -1, pointerEvents: 'none' }}
        >
          {backdrop}
        </div>
      ) : null}

      {/* Every slide shares one grid cell, so the section is as tall as the
       *  tallest slide and nothing jumps when the active one changes. */}
      <div ref={viewportRef} style={{ display: 'grid' }}>
        {slides.map((slide, i) => {
          const active = i === index;
          return (
            <article
              key={i}
              role={isCarousel ? 'group' : undefined}
              aria-roledescription={isCarousel ? 'slide' : undefined}
              aria-label={
                isCarousel ? t('hero.carousel.slidePosition', { index: i + 1, count }) : undefined
              }
              aria-hidden={isCarousel && !active ? true : undefined}
              inert={isCarousel && !active}
              style={{
                gridArea: '1 / 1',
                position: 'relative',
                isolation: 'isolate',
                minWidth: 0,
                ...(fade
                  ? {
                      opacity: active ? 1 : 0,
                      transition: reducedMotion ? 'none' : 'opacity 360ms ease',
                      pointerEvents: active ? 'auto' : 'none',
                    }
                  : {
                      transform: `translateX(${(i - index) * 100}%)`,
                      transition: 'transform 480ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }),
              }}
            >
              {renderSlide(slide, i, { active, hasControls })}
            </article>
          );
        })}
      </div>

      {hasControls ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 6,
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 11,
          }}
        >
          {showArrows ? (
            <div style={{ display: 'flex', gap: 7 }}>
              <button
                type="button"
                className="fh-slider-arrow"
                aria-label={t('hero.carousel.previous')}
                onClick={prev}
                {...part('pager', 0)}
                style={controlReset}
              >
                <ArrowDisc direction="prev" />
              </button>
              <button
                type="button"
                className="fh-slider-arrow"
                aria-label={t('hero.carousel.next')}
                onClick={next}
                {...part('pager', 1)}
                style={controlReset}
              >
                <ArrowDisc direction="next" />
              </button>
            </div>
          ) : null}
          {showDots ? (
            <div
              role="tablist"
              aria-label={t('hero.carousel.selector')}
              {...part('tablist')}
              style={{ display: 'flex', gap: 1 }}
            >
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  className="fh-slider-dot"
                  aria-selected={i === index}
                  aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
                  onClick={() => go(i)}
                  {...part('tab', i)}
                  style={controlReset}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'block',
                      width: i === index ? 14 : 6,
                      height: 6,
                      borderRadius: 999,
                    }}
                  />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {isCarousel ? (
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {t('hero.carousel.slidePosition', { index: index + 1, count })}
        </div>
      ) : null}
    </BlockRoot>
  );
}

/**
 * A slide's full-height content box: fills the viewport under the header,
 * applies the shared side gutters, and reserves room for the pinned
 * controls when {@code hasControls}.
 */
export function FullHeightFrame({
  hasControls,
  className,
  style,
  children,
}: {
  hasControls: boolean;
  /** Extra class — pair with `.fh-slider-frame` to override the gutters
   *  from a block stylesheet (inline padding would beat its breakpoints). */
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div
      className={className ? `fh-slider-frame ${className}` : 'fh-slider-frame'}
      data-controls={hasControls ? '' : undefined}
      style={{ display: 'flex', boxSizing: 'border-box', height: '100%', ...style }}
    >
      {children}
    </div>
  );
}

/** 21px translucent disc with a white chevron, inside the 24px hit area. */
function ArrowDisc({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 21,
        height: 21,
        borderRadius: 999,
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {direction === 'prev' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 18 15 12 9 6" />
        )}
      </svg>
    </span>
  );
}
